import { useMemo } from "react";
import { Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  Cell,
} from "recharts";
import { getChartColorSequence } from "@/theme/chartPalette";
import { formatListDate } from "@/utils/date-format";
import type { AccountsDashboardStats } from "../types";
import { formatINR } from "@/utils/app-formatters";

function fmtAxisInr(value: number, compact: boolean) {
  const n = Number(value);
  if (!compact) {
    return `₹${n.toLocaleString("en-IN")}`;
  }
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000) return `₹${(n / 100_000).toFixed(1)}L`;
  if (n >= 1_000) return `₹${Math.round(n / 1_000)}k`;
  return `₹${n}`;
}

function chartHeight(rowCount: number, compact: boolean, min = 200, max = 420) {
  const rowHeight = compact ? 32 : 36;
  const base = compact ? 64 : 80;
  return Math.min(max, Math.max(min, base + rowCount * rowHeight));
}

function truncateLabel(label: string, max: number) {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

const chartPaperSx = {
  p: { xs: 1.25, sm: 2 },
  height: "100%",
  minWidth: 0,
  overflow: "hidden",
};

type AccountsChartsRowProps = {
  stats: AccountsDashboardStats;
  showSabil: boolean;
  showLagat: boolean;
  showBooking: boolean;
  showNiyaaz: boolean;
  showYearlyNiyaaz: boolean;
};

export default function AccountsChartsRow({
  stats,
  showSabil,
  showLagat,
  showBooking,
  showNiyaaz,
  showYearlyNiyaaz,
}: AccountsChartsRowProps) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const isMedium = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const colors = useMemo(() => getChartColorSequence(theme), [theme]);

  const labelMax = isSmall ? 12 : isMedium ? 18 : 22;
  const yAxisMonthWidth = isSmall ? 52 : isMedium ? 72 : 88;
  const yAxisModeWidth = isSmall ? 48 : 72;
  const yAxisPurposeWidth = isSmall ? 72 : isMedium ? 96 : 120;
  const chartMargin = {
    left: isSmall ? 0 : 4,
    right: isSmall ? 8 : 16,
    top: 4,
    bottom: isSmall ? 8 : 4,
  };

  const monthBars = (stats.byMonth ?? []).map((row) => ({
    ...row,
    monthLabel: truncateLabel(formatListDate(dayjs(`${row.month}-01`)), labelMax),
    fullMonthLabel: formatListDate(dayjs(`${row.month}-01`)),
  }));

  const paymentModeBars = (stats.paymentModeBreakdown ?? []).map((row) => ({
    mode: truncateLabel(row.mode, labelMax),
    fullMode: row.mode,
    amount: row.amount,
    count: row.count,
  }));

  const lagatPurposeBars = (stats.lagatByPurpose ?? []).map((row) => ({
    purpose: truncateLabel(row.purposeName, labelMax),
    fullPurpose: row.purposeName,
    amount: row.amount,
    count: row.count,
  }));

  const bookingTypeBars = (stats.bookingByType ?? []).map((row) => ({
    type: row.type === "DEPOSIT" ? "Deposit" : "Rent",
    amount: row.amount,
    count: row.count,
  }));

  const hasMonthData = monthBars.some(
    (row) =>
      (row.sabil ?? 0) > 0 ||
      (row.lagat ?? 0) > 0 ||
      (row.booking ?? 0) > 0 ||
      (row.niyaaz ?? 0) > 0 ||
      (row.yearlyNiyaaz ?? 0) > 0
  );

  const axisTick = { fontSize: isSmall ? 10 : 11 };

  return (
    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
      {hasMonthData && (
        <Grid size={{ xs: 12 }}>
          <Paper sx={chartPaperSx}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
              Monthly collection by stream
            </Typography>
            <ResponsiveContainer
              width="100%"
              height={chartHeight(monthBars.length, isSmall, isSmall ? 220 : 240, 480)}
            >
              <BarChart data={monthBars} layout="vertical" margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => fmtAxisInr(Number(v), isSmall)}
                  tick={axisTick}
                />
                <YAxis
                  type="category"
                  dataKey="monthLabel"
                  width={yAxisMonthWidth}
                  tick={axisTick}
                />
                <Tooltip
                  formatter={(v) => formatINR(Number(v))}
                  labelFormatter={(_, payload) => {
                    if (!payload || payload.length === 0) return "";
                    return String(payload[0].payload.fullMonthLabel ?? "");
                  }}
                />
                <Legend wrapperStyle={{ fontSize: isSmall ? 11 : 12 }} />
                {showSabil && <Bar dataKey="sabil" fill={colors[0]} name="Sabil" stackId="a" />}
                {showLagat && <Bar dataKey="lagat" fill={colors[1]} name="Lagat" stackId="a" />}
                {showBooking && (
                  <Bar dataKey="booking" fill={colors[2]} name="Booking" stackId="a" />
                )}
                {showNiyaaz && (
                  <Bar dataKey="niyaaz" fill={colors[3]} name="Niyaaz Event" stackId="a" />
                )}
                {showYearlyNiyaaz && (
                  <Bar dataKey="yearlyNiyaaz" fill={colors[4]} name="Yearly Niyaaz" stackId="a" />
                )}
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      )}

      {paymentModeBars.length > 0 && (
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={chartPaperSx}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
              Collection by payment mode
            </Typography>
            <ResponsiveContainer width="100%" height={chartHeight(paymentModeBars.length, isSmall)}>
              <BarChart data={paymentModeBars} layout="vertical" margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => fmtAxisInr(Number(v), isSmall)}
                  tick={axisTick}
                />
                <YAxis type="category" dataKey="mode" width={yAxisModeWidth} tick={axisTick} />
                <Tooltip
                  formatter={(v) => formatINR(Number(v))}
                  labelFormatter={(_, payload) => {
                    if (!payload || payload.length === 0) return "";
                    return String(payload[0].payload.fullMode ?? "");
                  }}
                />
                <Bar dataKey="amount" fill={colors[3]} name="Amount" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      )}

      {showLagat && lagatPurposeBars.length > 0 && (
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={chartPaperSx}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
              Lagat by purpose
            </Typography>
            <ResponsiveContainer
              width="100%"
              height={chartHeight(lagatPurposeBars.length, isSmall)}
            >
              <BarChart data={lagatPurposeBars} layout="vertical" margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => fmtAxisInr(Number(v), isSmall)}
                  tick={axisTick}
                />
                <YAxis
                  type="category"
                  dataKey="purpose"
                  width={yAxisPurposeWidth}
                  tick={axisTick}
                />
                <Tooltip
                  formatter={(v) => formatINR(Number(v))}
                  labelFormatter={(_, payload) => {
                    if (!payload || payload.length === 0) return "";
                    return String(payload[0].payload.fullPurpose ?? "");
                  }}
                />
                <Bar dataKey="amount" name="Amount" radius={[0, 4, 4, 0]}>
                  {lagatPurposeBars.map((entry, index) => (
                    <Cell
                      key={`${entry.fullPurpose}-${index}`}
                      fill={colors[index % colors.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      )}

      {showBooking && bookingTypeBars.length > 0 && (
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={chartPaperSx}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
              Booking receipts by type
            </Typography>
            <ResponsiveContainer
              width="100%"
              height={chartHeight(bookingTypeBars.length, isSmall, 140, 200)}
            >
              <BarChart data={bookingTypeBars} layout="vertical" margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => fmtAxisInr(Number(v), isSmall)}
                  tick={axisTick}
                />
                <YAxis type="category" dataKey="type" width={yAxisModeWidth} tick={axisTick} />
                <Tooltip formatter={(v) => formatINR(Number(v))} />
                <Bar dataKey="amount" fill={colors[4]} name="Amount" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
}
